import React from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { doc, Timestamp, updateDoc } from 'firebase/firestore';
import { useRouter } from 'next/router';
import { logEvent } from 'firebase/analytics';
import posthog from 'posthog-js';
import { firestore, invariant, loadAnalytics } from '../../../lib';
import { Button } from '../../commons';
import { Input } from '../../inputs';
import { Application } from '../../../models';

interface ESignatureFormProps {
  application: Application;
  isReadOnly?: boolean;
}

const toastError = (message: string) =>
  toast.error(message, {
    position: 'top-right',
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
  });

export default function ESignatureForm({
  application,
  isReadOnly = false,
}: ESignatureFormProps) {
  const router = useRouter();
  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    try {
      invariant(
        formData.get('e-signature')?.toString()?.trim(),
        'You must sign your application'
      );
    } catch (err) {
      return toastError(err.message);
    }
    try {
      await updateDoc(doc(firestore, 'applications', application?.id), {
        lastUpdated: Timestamp.fromDate(new Date()),
        step: '10',
        signature: formData.get('e-signature')?.toString()?.trim(),
      });
    } catch (err) {
      return toastError(
        'An error has occurred, please try again later. CODE: E101'
      );
    }

    try {
      await axios.post('/api/send-mail', {
        email: application?.applicant?.email ?? '',
        template: 'submission',
        name: `${application?.applicant?.firstName ?? ''} ${
          application?.applicant?.middleName ?? ''
        } ${application?.applicant?.surname ?? ''}`,
        category: `${application?.category ?? ''}`,
      });
    } catch (err) {
      return toastError(
        'An error has occurred, please try again later. CODE: E102'
      );
    }

    try {
      logEvent(loadAnalytics(), 'step_completed', {
        step: '10',
        finishedTime: new Date().toISOString(),
      });
      try {
        posthog?.capture(
          'step_completed',
          {
            step: '10',
            finishedTime: new Date().toISOString(),
          },
          { transport: 'sendBeacon' }
        );
      } catch (err) {
        posthog.captureException(err);
      }
      await router.push(
        '/request-scholarship/[...id]',
        '/request-scholarship/11'
      );
    } catch (err) {
      return toastError(err.message);
    }
  };
  return (
    <form method="post" onSubmit={onSubmit}>
      <Input
        type="text"
        name="e-signature"
        label="E-Signature"
        placeholder="E-Signature"
        required
        defaultValue={application?.signature ?? ''}
        readOnly={isReadOnly}
        disabled={isReadOnly}
      />
      {isReadOnly ? null : (
        <Button type="submit" color="blue" className="w-full">
          Submit
        </Button>
      )}
    </form>
  );
}
